import { Typography } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { JobconfigSupplierComparisonArrowIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import {
  JobComparatorPart,
  JobComparatorSvc,
  JobEstimator,
  JobEstimatorComparatorItem,
  JobSpec,
  SearchScopeEnum,
} from 'domains/jobconfigDomain';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { generateRFQRouterStr } from 'utils/routingRedirection';

interface ISourceComparisonLabel {
  maxLabel: number;
  showJobEstimator: {
    list: JobEstimatorComparatorItem[];
    partLabel: JobComparatorPart[];
    svcLabel: JobComparatorSvc[];
    rush?: boolean;
  };
  clickToggle: (x: string) => void;
  isClickToggle: (x: string) => boolean;
  boxHeight: number;
  jobEstimator: JobEstimator | undefined;
  canAddToCart: boolean;
  spec?: JobSpec;
}

export const SourceComparisonLabel = (props: ISourceComparisonLabel) => {
  const { maxLabel } = props;
  return (
    <RevxFlexLayout
      direction="column"
      className="comparison-drawer-label"
      style={{
        maxWidth: `${maxLabel}px`,
        minWidth: '220px',
      }}
    >
      <SourceComparisonLabelTitle {...props} />

      <SourceComparisonLabelPart {...props} />

      <SourceComparisonLabelSvc {...props} />

      <SourceComparisonLabelSubtotalMarkup />

      <SourceComparisonLabelSubtotal />

      <SourceComparisonLabelShipping {...props} />

      <SourceComparisonLabelAction {...props} />
    </RevxFlexLayout>
  );
};

export const SourceComparisonLabelTitle = (props: ISourceComparisonLabel) => {
  const { jobEstimator, showJobEstimator } = props;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const configItem = useAppSelector((state) => state.jobConfig.configItem);
  const isRush = useMemo(() => {
    return Boolean(showJobEstimator?.rush);
  }, [showJobEstimator]);
  return (
    <>
      <RevxFlexLayout
        className={`comparison-drawer-rfq`}
        justify="justify-center"
        align="center"
      >
        <RevxButton
          type="primary"
          className="comparison-drawer-rfq-button"
          // disabled={showJobEstimator.list.length === 1}
          // hidden={rush}
          onClick={async () => {
            if (jobEstimator?.jobConfigId) {
              try {
                const { id } = await dispatch.jobEstimator.init({
                  jobConfigId: jobEstimator?.jobConfigId,
                  isProxied: configItem.isProxied,
                });
                history.push(generateRFQRouterStr(id));
              } catch {}
            }
          }}
        >
          RFQ
        </RevxButton>
      </RevxFlexLayout>
      <RevxFlexLayout
        align="center"
        justify="right"
        hidden={!isRush}
        className={`comparison-drawer-part-service comparison-drawer-part-service-label ${
          isRush ? 'comparison-drawer-rush' : ''
        }`}
        style={{
          height: '38px',
          textAlign: 'right',
        }}
      >
        <span>User Requested Arrival Date</span>
      </RevxFlexLayout>
    </>
  );
};

interface ISourceComparisonLabelPart {
  showJobEstimator: {
    list: JobEstimatorComparatorItem[];
    partLabel: JobComparatorPart[];
    svcLabel: JobComparatorSvc[];
  };
  clickToggle: (x: string) => void;
  isClickToggle: (x: string) => boolean;
  spec?: JobSpec;
}

export const SourceComparisonLabelPart = (
  props: ISourceComparisonLabelPart
) => {
  const { showJobEstimator, clickToggle, isClickToggle, spec } = props;
  return (
    <>
      {showJobEstimator?.partLabel?.map((part, index) => {
        return (
          <RevxFlexLayout
            direction="column"
            className="comparison-drawer-part"
            key={part.skuId}
          >
            <RevxFlexLayout
              justify="right"
              className="comparison-drawer-part-label"
              onClick={() => clickToggle(part.skuName)}
            >
              <span>
                {part.pageCount
                  ? `${part.skuName} (${part.pageCount})`
                  : part.skuName}
              </span>
              <JobconfigSupplierComparisonArrowIcon
                className={
                  !isClickToggle(part.skuName)
                    ? 'comparison-drawer-check-icon'
                    : ''
                }
              />
            </RevxFlexLayout>
            <RevxFlexLayout
              align="center"
              justify="right"
              className="comparison-drawer-part-press comparison-drawer-part-press-label"
              hidden={!isClickToggle(part.skuName)}
            >
              <span>Press</span>
            </RevxFlexLayout>
            <RevxFlexLayout
              align="center"
              justify="right"
              className="comparison-drawer-part-service comparison-drawer-part-service-label"
              hidden={!isClickToggle(part.skuName)}
              style={
                {
                  // marginBottom: part.svcs.length ? '' : '10px',
                }
              }
            >
              <span>Paper</span>
            </RevxFlexLayout>
            {spec?.parts[index].paperSearchScope ===
              SearchScopeEnum.MY_PAPER && (
              <RevxFlexLayout
                justify="right"
                className=" comparison-drawer-part-label no-background"
              >
                <span>Find comparable paper {'>'}</span>
              </RevxFlexLayout>
            )}

            {part.svcs.map((svc) => {
              return (
                <RevxFlexLayout
                  key={svc.name + svc.charge}
                  align="center"
                  justify="right"
                  className="comparison-drawer-part-service comparison-drawer-part-service-label"
                  hidden={!isClickToggle(part.skuName)}
                >
                  <span>{svc.name}</span>
                </RevxFlexLayout>
              );
            })}
          </RevxFlexLayout>
        );
      })}
    </>
  );
};

interface ISourceComparisonLabelSvc {
  showJobEstimator: {
    list: JobEstimatorComparatorItem[];
    partLabel: JobComparatorPart[];
    svcLabel: JobComparatorSvc[];
  };
  clickToggle: (x: string) => void;
  isClickToggle: (x: string) => boolean;
  boxHeight: number;
}

export const SourceComparisonLabelSvc = (props: ISourceComparisonLabelSvc) => {
  const { showJobEstimator, clickToggle, isClickToggle, boxHeight } = props;
  return (
    <RevxFlexLayout direction="column" className="comparison-drawer-wrapper">
      <RevxFlexLayout
        justify="right"
        className="comparison-drawer-part-label comparison-drawer-wrapper-label"
        onClick={() => clickToggle('Services')}
      >
        <span>Services</span>
        <JobconfigSupplierComparisonArrowIcon
          className={
            !isClickToggle('Services') ? 'comparison-drawer-check-icon' : ''
          }
        />
      </RevxFlexLayout>
      {showJobEstimator.svcLabel.map((svc, idx) => {
        return (
          <RevxFlexLayout
            align="center"
            justify="right"
            key={svc.charge + svc.name}
            className="comparison-drawer-wrapper-service comparison-drawer-wrapper-service-label"
            hidden={!isClickToggle('Services')}
            style={{
              marginTop: idx === 0 ? '16px' : '',
            }}
          >
            <Typography.Title
              style={{
                fontSize: '13px',
                marginBottom: 0,
                maxWidth: `150px`,
              }}
              ellipsis={{
                tooltip: svc.name,
              }}
            >
              {svc.name}
            </Typography.Title>
          </RevxFlexLayout>
        );
      })}
      <RevxFlexLayout
        align="top"
        justify="right"
        className="comparison-drawer-wrapper-service comparison-drawer-wrapper-service-parcel"
        hidden={!isClickToggle('Services')}
        style={{
          paddingTop: '10px',
          height: `${boxHeight}px`,
          marginBottom: '16px',
          minHeight: '0px',
        }}
      >
        <span>Box Charge</span>
      </RevxFlexLayout>
    </RevxFlexLayout>
  );
};

export const SourceComparisonLabelSubtotal = () => {
  return (
    <RevxFlexLayout justify="right" className="comparison-drawer-subtotal">
      <span>Subtotal</span>
    </RevxFlexLayout>
  );
};

export const SourceComparisonLabelSubtotalMarkup = () => {
  return (
    <RevxFlexLayout
      align="center"
      justify="right"
      className="comparison-drawer-wrapper-service comparison-drawer-wrapper-service-label"
      style={{
        height: '60px',
      }}
    >
      <span>Profit Margin/Discount</span>
    </RevxFlexLayout>
  );
};

interface ISourceComparisonLabelShipping {
  clickToggle: (x: string) => void;
  isClickToggle: (x: string) => boolean;
}

export const SourceComparisonLabelShipping = (
  props: ISourceComparisonLabelShipping
) => {
  const { clickToggle, isClickToggle } = props;
  return (
    <RevxFlexLayout direction="column" className="comparison-drawer-wrapper">
      <RevxFlexLayout
        justify="right"
        className="comparison-drawer-part-label comparison-drawer-wrapper-label"
        onClick={() => clickToggle('Shipping Total')}
      >
        <span>Shipping Total</span>
        <JobconfigSupplierComparisonArrowIcon
          className={
            !isClickToggle('Shipping Total')
              ? 'comparison-drawer-check-icon'
              : ''
          }
        />
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="right"
        className="comparison-drawer-part-service comparison-drawer-part-service-label"
        hidden={!isClickToggle('Shipping Total')}
      >
        <span>Shipping</span>
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="right"
        className="comparison-drawer-part-service comparison-drawer-part-service-label"
        hidden={!isClickToggle('Shipping Total')}
      >
        <span>Shipping Markup/Discount</span>
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="right"
        className="comparison-drawer-part-service comparison-drawer-part-service-label"
        hidden={!isClickToggle('Shipping Total')}
        style={{
          height: '38px',
        }}
      >
        <span>Date Ready</span>
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="right"
        className="comparison-drawer-part-service comparison-drawer-part-service-label"
        hidden={!isClickToggle('Shipping Total')}
        style={{
          height: '38px',
        }}
      >
        <span>Arrival Date</span>
      </RevxFlexLayout>
    </RevxFlexLayout>
  );
};

export const SourceComparisonLabelAction = (props: ISourceComparisonLabel) => {
  const { showJobEstimator } = props;
  const isRush = useMemo(() => {
    return Boolean(showJobEstimator.rush);
  }, [showJobEstimator]);
  return (
    <RevxFlexLayout
      align="center"
      justify="right"
      className="comparison-drawer-part-label comparison-drawer-wrapper-label comparison-drawer-total"
    >
      <label>
        {isRush ? (
          <Trans i18nKey="jobConfig.stepViewQuote.estimatedJobCosts"></Trans>
        ) : (
          <Trans i18nKey="jobConfig.stepViewQuote.jobNetTotal"></Trans>
        )}
      </label>
    </RevxFlexLayout>
  );
};
